import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Download, FileText, Plus, RefreshCw, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/shared/store/page';
import { useToast } from '@/shared/toast/toast-context';
import { downloadAuthenticated } from '@/shared/api/client';
import { usePathologyList } from './api';

const PAGE_SIZE = 20;

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function PathologyListPage(): JSX.Element {
  usePageTitle('Resultado de patología', 'Documentos clínicos · Constancias de entrega');
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, isError, error, refetch } = usePathologyList({
    page,
    pageSize: PAGE_SIZE,
    ...(debounced.trim() === '' ? {} : { q: debounced.trim() }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = total === 0 ? 1 : Math.ceil(total / PAGE_SIZE);

  const download = async (id: string): Promise<void> => {
    try {
      await downloadAuthenticated(`/api/pathology/${id}/pdf`, `constancia-patologia-${id}.pdf`);
    } catch {
      showToast({ kind: 'danger', title: 'No se pudo descargar la constancia' });
    }
  };

  const rows = useMemo(() => items, [items]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end gap-2.5 flex-wrap">
        <div className="flex flex-col gap-1.5 flex-1 min-w-[240px]">
          <Label htmlFor="pat-q">Buscar</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <Input
              id="pat-q"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Paciente, documento, patología o quien recibe…"
              className="pl-9"
            />
          </div>
        </div>
        <Button onClick={() => navigate('/patologia/nueva')}>
          <Plus className="w-4 h-4" /> Nueva entrega
        </Button>
      </div>

      {isError && (
        <div className="bg-danger-soft border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">No fue posible cargar las constancias</div>
            <div className="text-sm text-red-700">{error.message}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            <RefreshCw className="w-4 h-4" /> Reintentar
          </Button>
        </div>
      )}

      <Card>
        <CardHeader>
          <div>
            <CardTitle>Constancias de entrega</CardTitle>
            <CardDescription>
              {isLoading
                ? 'Cargando…'
                : `${total} ${total === 1 ? 'constancia' : 'constancias'}`}
              {isFetching && !isLoading ? ' · actualizando…' : ''}
            </CardDescription>
          </div>
        </CardHeader>

        {isLoading ? (
          <div className="p-6 flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-12 rounded-md bg-surface-alt animate-pulse" />
            ))}
          </div>
        ) : !isError && rows.length === 0 ? (
          <div className="p-10 text-center flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-surface-alt grid place-items-center text-text-muted">
              <FileText className="w-6 h-6" />
            </div>
            <div className="text-text font-semibold">Sin constancias</div>
            <p className="text-text-muted text-sm max-w-sm">
              Aún no hay constancias de entrega. Crea una nueva para registrar la recepción de un
              resultado de patología.
            </p>
            <Button onClick={() => navigate('/patologia/nueva')}>
              <Plus className="w-4 h-4" /> Nueva entrega
            </Button>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Paciente</TableHead>
                <TableHead>Patología</TableHead>
                <TableHead>Recibió</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="w-[130px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>
                    <div className="font-semibold text-text">{r.pacienteNombre}</div>
                    <div className="text-text-muted text-xs font-mono">{r.pacienteDocumento}</div>
                  </TableCell>
                  <TableCell>{r.patologia}</TableCell>
                  <TableCell>
                    <div className="text-sm">{r.nombreReceptor}</div>
                    <div className="text-text-muted text-xs">{r.parentescoReceptor}</div>
                  </TableCell>
                  <TableCell>
                    <span className="text-text-muted text-sm">
                      {new Date(r.firmadoEn).toLocaleDateString('es-CO')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="secondary" onClick={() => void download(r.id)}>
                      <Download className="w-4 h-4" /> Descargar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!isLoading && !isError && total > 0 && (
          <CardFooter className="justify-between">
            <div className="text-text-muted text-sm">
              Página {page} de {totalPages} · {total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
